import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import EmailPackModal from "../components/EmailPackModal";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Plus, FileDown, Mail } from "lucide-react";
import CertList from "./squad/CertList";
import ExerciseList from "./squad/ExerciseList";
import { CourseRow, AssignPanel } from "./squad/CourseList";

const TOAST_DURATION_MS = 4000;
const HTTP_NOT_FOUND = 404;

const TRACK_COLOR = {
  operator: "var(--cyan)", engineer: "var(--amber)",
  hunter: "var(--green)", leader: "var(--red)",
};

function Stat({ label, v, c }) {
  return (
    <div className="border border-[var(--border)] p-3 min-w-[80px]">
      <div className="font-mono text-2xl" style={{ color: c }}>{v}</div>
      <div className="font-mono text-[10px] text-[var(--text-2)] tracking-widest uppercase mt-0.5">{label}</div>
    </div>
  );
}

function Toast({ toast }) {
  if (!toast) return null;
  const cls = toast.kind === "ok"
    ? "border-[var(--green)] text-[var(--green)] bg-[var(--surface)]"
    : "border-[var(--red)] text-[var(--red)] bg-[var(--surface)]";
  return (
    <div className={`fixed bottom-6 right-6 border p-4 font-mono text-sm tracking-wide ${cls}`} data-testid="sd-toast">
      {toast.msg}
    </div>
  );
}

export default function SquadDetail() {
  const { user } = useAuth();
  const { traineeId } = useParams();
  const nav = useNavigate();
  const isLeader = user?.role === "leader" || user?.role === "org_admin" || user?.role === "admin";

  const [data, setData] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/squad/${traineeId}`);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === HTTP_NOT_FOUND) nav("/squad");
    }
  }, [traineeId, nav]);

  useEffect(() => {
    if (!isLeader) return;
    load();
  }, [isLeader, load]);

  const fireToast = useCallback((kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  const assign = useCallback(async (course) => {
    setBusyId(course.id);
    try {
      await api.post(`/squad/${traineeId}/assign`, { course_id: course.id });
      fireToast("ok", `Assigned ${course.code} — ${course.title}`);
      await load();
    } catch (e) {
      fireToast("err", e?.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  }, [traineeId, fireToast, load]);

  const unassign = useCallback(async (course) => {
    if (!window.confirm(`Unassign ${course.code}? (only allowed if progress=0)`)) return;
    setBusyId(course.id);
    try {
      await api.delete(`/squad/${traineeId}/assign/${course.id}`);
      fireToast("ok", `Unassigned ${course.code}`);
      await load();
    } catch (e) {
      fireToast("err", e?.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  }, [traineeId, fireToast, load]);

  if (!isLeader) {
    return (
      <div className="min-h-screen bg-[var(--bg)]"><TopNav />
        <div className="max-w-3xl mx-auto px-6 py-16 font-mono text-[var(--amber)]">// ACCESS DENIED — Leader/Admin only</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[var(--bg)]"><TopNav />
        <div className="max-w-7xl mx-auto px-6 py-10 text-[var(--text-2)]">Loading…</div>
      </div>
    );
  }

  const { trainee, enrollments, certifications, exercises, assignable_courses: assignable } = data;
  const avg = enrollments.length
    ? Math.round(enrollments.reduce((a, e) => a + (e.progress || 0), 0) / enrollments.length)
    : 0;
  const completed = enrollments.filter((e) => e.status === "completed").length;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/squad" className="inline-flex items-center gap-2 font-mono text-xs text-[var(--text-2)] hover:text-[var(--cyan)] uppercase tracking-widest mb-4" data-testid="back-to-squad">
          <ArrowLeft size={14} /> Squad
        </Link>

        <div className="flat-card p-7 scanlines">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-mono text-xs text-[var(--text-2)] uppercase tracking-widest">// TRAINEE_PROFILE</div>
              <h1 className="font-display font-bold text-4xl uppercase mt-1" data-testid="sd-name">{trainee.name}</h1>
              <div className="font-mono text-sm text-[var(--text-2)] mt-1">{trainee.email}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="tag" style={{ color: TRACK_COLOR[trainee.track], borderColor: TRACK_COLOR[trainee.track] }}>
                  TRK / {trainee.track}
                </span>
                <span className="tag">ROLE / {trainee.role}</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat label="Avg" v={`${avg}%`} c="var(--cyan)" />
              <Stat label="Done" v={completed} c="var(--green)" />
              <Stat label="Certs" v={certifications.length} c="var(--amber)" />
            </div>
          </div>
          <a
            href={`${API}/trainees/${trainee.id}/compliance-pack.pdf`}
            className="btn-primary inline-flex items-center gap-2 mt-4"
            data-testid="sd-download-pack"
          >
            <FileDown size={14} /> Download Compliance Pack (PDF)
          </a>
          <button
            onClick={() => setEmailOpen(true)}
            className="btn-ghost inline-flex items-center gap-2 mt-4 ml-2"
            data-testid="sd-email-pack"
          >
            <Mail size={14} /> Email to procurement contact
          </button>
        </div>

        <EmailPackModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          endpoint={`/trainees/${trainee.id}/compliance-pack/email`}
          subjectName={trainee.name}
        />

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2 flat-card p-6" data-testid="sd-courses-card">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="overline">// 01_TRAINING</div>
                <h2 className="font-display font-bold text-2xl uppercase mt-1">Assigned Courses</h2>
              </div>
              <button
                onClick={() => setAssignOpen((v) => !v)}
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="sd-assign-toggle"
              >
                <Plus size={14} /> {assignOpen ? "Close" : "Assign"} Training
              </button>
            </div>

            {assignOpen && (
              <AssignPanel assignable={assignable} busyId={busyId} onAssign={assign} />
            )}

            <div className="space-y-3">
              {enrollments.length === 0 && (
                <div className="text-sm text-[var(--text-2)]">No courses assigned yet.</div>
              )}
              {enrollments.map((e) => (
                <CourseRow key={e.id} enrollment={e} busyId={busyId} onUnassign={unassign} />
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <CertList certifications={certifications} />
            <ExerciseList exercises={exercises} />
          </div>
        </div>

        <Toast toast={toast} />
      </main>
    </div>
  );
}
